import React, { useState, useEffect } from 'react';
import { Modal, Form, ProgressBar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaArrowRight, FaLock, FaTimes, FaHistory } from 'react-icons/fa';
import { getCurrentQuestionnaire, saveAnswer, completeQuestionnaire } from '../actions/questionnaireActions';
import { QUESTIONNAIRE_COMPLETE_RESET } from '../constants/questionnaireConstants';
import GradientButton from './GradientButton';

function QuestionnaireModal({ show, onComplete }) {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [localError, setLocalError] = useState('');
    
    const dispatch = useDispatch();
    
    const questionnaireCurrent = useSelector(state => state.questionnaireCurrent);
    const { questionnaire, loading, error } = questionnaireCurrent;
    
    const questionnaireComplete = useSelector(state => state.questionnaireComplete);
    const { success: completeSuccess, result, error: completeError } = questionnaireComplete;

    useEffect(() => {
        if (show) {
            dispatch(getCurrentQuestionnaire());
        }
    }, [dispatch, show]);

    useEffect(() => {
        if (questionnaire?.current_answers) {
            const existingAnswers = {};
            
            // Załaduj tylko current_answers (mogą być null)
            Object.keys(questionnaire.current_answers).forEach(key => {
                const answer = questionnaire.current_answers[key];
                // Załaduj tylko jeśli answer nie jest null
                if (answer && answer.answer_value) {
                    existingAnswers[key] = answer.answer_value;
                }
            });
            
            // Ustaw odpowiedzi tylko jeśli są jakieś dane
            if (Object.keys(existingAnswers).length > 0) {
                setAnswers(existingAnswers);
            }
        }
    }, [questionnaire]);

    useEffect(() => {
        if (completeSuccess) {
            alert(`✅ Kwestionariusz ukończony!\n\nPrzyznano ${result.vacation_days_granted} dni urlopu.`);
            dispatch({ type: QUESTIONNAIRE_COMPLETE_RESET });
            if (onComplete) onComplete();
        }
    }, [completeSuccess, result, dispatch, onComplete]);

    // Reset modal state when closed
    useEffect(() => {
        if (!show) {
            setCurrentQuestionIndex(0);
            setAnswers({});
            setLocalError('');
        }
    }, [show]);

    if (!questionnaire || !questionnaire.has_questionnaire) {
        return null;
    }

    const { questions, current_answers, previous_answers } = questionnaire;
    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

    // ✨ Funkcja formatowania odpowiedzi dla wyświetlenia
    const formatPreviousAnswer = (question, answerValue) => {
        if (!answerValue) return null;
        
        const qType = question.question_type;
        
        if (qType === 'birth_date' || qType === 'vocational_start_date') {
            const monthName = new Date(2000, answerValue.month - 1).toLocaleDateString('pl-PL', { month: 'long' });
            return `${monthName} ${answerValue.year}`;
        }
        
        if (qType === 'current_class') {
            return `Klasa ${answerValue.value}`;
        }
        
        if (qType === 'changed_workplace') {
            return answerValue.value === 'yes' ? 'Tak, zmieniłem pracodawcę' : 'Nie, pracuję u tego samego pracodawcy';
        }
        
        if (qType === 'vacation_used' || qType === 'vacation_remaining') {
            if (answerValue.type === 'dont_remember') return 'Nie pamiętam';
            return `${answerValue.value} dni (${answerValue.type === 'exact' ? 'dokładnie' : 'około'})`;
        }
        
        return JSON.stringify(answerValue);
    };

    // ✨ Funkcja walidacji odpowiedzi
    const validateAnswer = (question, answer) => {
        if (!answer) return false;

        const questionType = question.question_type;

        if (questionType === 'vacation_used' || questionType === 'vacation_remaining') {
            if (!answer.type) return false;
            if ((answer.type === 'exact' || answer.type === 'approximate')) {
                if (answer.value === undefined || answer.value === null || answer.value === '') {
                    return false;
                }
            }
            return true;
        }

        if (questionType === 'birth_date' || questionType === 'vocational_start_date') {
            return answer.year && answer.month;
        }

        if (questionType === 'current_class' || 
            questionType === 'repeated_class' || 
            questionType === 'changed_workplace') {
            return answer.value;
        }

        return true;
    };

    // ✨ Funkcja do sprawdzenia które pytania nie mają odpowiedzi
    const getUnansweredQuestions = () => {
        return questions.filter(q => !validateAnswer(q, answers[q.question_key]));
    };

    const handleAnswerChange = (questionKey, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionKey]: value
        }));
        setLocalError('');
    };

    const isQuestionLocked = (questionKey) => {
        const answer = current_answers?.[questionKey];
        return answer && answer.is_editable === false;
    };

    // ✨ NOWA: Funkcja do użycia poprzedniej odpowiedzi
    const applyPreviousAnswer = (questionKey) => {
        const prevAnswer = previous_answers?.[questionKey];
        if (prevAnswer && prevAnswer.answer_value) {
            handleAnswerChange(questionKey, prevAnswer.answer_value);
        }
    };

    const handleNext = async () => {
        let currentAnswer = answers[currentQuestion.question_key];
        
        if (!validateAnswer(currentQuestion, currentAnswer)) {
            setLocalError('Musisz odpowiedzieć na to pytanie');
            return;
        }

        await dispatch(saveAnswer(currentQuestion.question_key, currentAnswer));

        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            setLocalError('');
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
            setLocalError('');
        }
    };

    const handleSubmit = async () => {
        const lastQuestion = questions[currentQuestionIndex];
        const lastAnswer = answers[lastQuestion.question_key];
        
        if (!validateAnswer(lastQuestion, lastAnswer)) {
            setLocalError('Musisz odpowiedzieć na to pytanie przed wysłaniem');
            return;
        }
        
        await dispatch(saveAnswer(lastQuestion.question_key, lastAnswer));
        
        const unansweredQuestions = getUnansweredQuestions();
        
        if (unansweredQuestions.length > 0) {
            const questionNumbers = unansweredQuestions
                .map(q => questions.indexOf(q) + 1)
                .join(', ');
            
            setLocalError(
                `❌ Musisz odpowiedzieć na wszystkie pytania\n\n` +
                `Brakuje odpowiedzi na pytania: ${questionNumbers}\n\n` +
                `(${questions.length - unansweredQuestions.length}/${questions.length})`
            );
            return;
        }

        if (window.confirm('Czy na pewno chcesz wysłać kwestionariusz? Po wysłaniu nie będziesz mógł edytować odpowiedzi.')) {
            dispatch(completeQuestionnaire());
        }
    };

    const handleCloseModal = () => {
        const unanswered = getUnansweredQuestions();
        
        if (unanswered.length > 0) {
            const answered = questions.length - unanswered.length;
            if (window.confirm(
                `Zamknąć kwestionariusz?\n\nUdzieliłeś odpowiedzi na ${answered}/${questions.length} pytań.\n\n` +
                `Po ponownym otwarciu będziesz mógł kontynuować z ostatniego miejsca.`
            )) {
                if (onComplete) onComplete();
            }
        } else {
            if (window.confirm('Zamknąć kwestionariusz? Wszystkie odpowiedzi zostały zapisane.')) {
                if (onComplete) onComplete();
            }
        }
    };
    
    const renderQuestionInput = () => {
        const questionKey = currentQuestion.question_key;
        const questionType = currentQuestion.question_type;
        
        // Bezpieczne pobieranie odpowiedzi
        const currentAnswerData = current_answers?.[questionKey];
        const currentAnswer = answers[questionKey] || currentAnswerData?.answer_value;
        const locked = isQuestionLocked(questionKey);
        
        // Pobierz poprzednią odpowiedź
        const previousAnswer = previous_answers?.[questionKey];
        const hasPreviousAnswer = previousAnswer && previousAnswer.answer_value && !locked;
        const hasCurrentAnswer = currentAnswer && Object.keys(currentAnswer).length > 0;

        // Data urodzenia
        if (questionType === 'birth_date') {
            return (
                <div>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-white fw-semibold">Rok urodzenia</Form.Label>
                        <Form.Control
                            type="number"
                            min="1900"
                            max={new Date().getFullYear()}
                            value={currentAnswer?.year || ''}
                            onChange={(e) => handleAnswerChange(questionKey, {
                                ...currentAnswer,
                                year: parseInt(e.target.value)
                            })}
                            disabled={locked}
                            style={{
                                borderRadius: '15px',
                                border: '2px solid #e0e0e0',
                                padding: '0.75rem'
                            }}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="text-white fw-semibold">Miesiąc urodzenia</Form.Label>
                        <Form.Select
                            value={currentAnswer?.month || ''}
                            onChange={(e) => handleAnswerChange(questionKey, {
                                ...currentAnswer,
                                month: parseInt(e.target.value)
                            })}
                            disabled={locked}
                            style={{
                                borderRadius: '15px',
                                border: '2px solid #e0e0e0',
                                padding: '0.75rem'
                            }}
                        >
                            <option value="">Wybierz miesiąc</option>
                            {Array.from({ length: 12 }, (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {new Date(2000, i).toLocaleDateString('pl-PL', { month: 'long' })}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    {locked && (
                        <div 
                            className="mt-3 p-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(33, 212, 253, 0.1) 0%, rgba(183, 33, 255, 0.1) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(33, 212, 253, 0.2)'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <FaLock className="me-2" style={{ color: '#21D4FD' }} />
                                <small className="text-white mb-0">
                                    Ta odpowiedź jest zablokowana. Jeśli chcesz ją zmienić, skontaktuj się z administratorem.
                                </small>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Klasa
        if (questionType === 'current_class') {
            return (
                <div>
                    {/* POPRZEDNIA ODPOWIEDŹ */}
                    {hasPreviousAnswer && !hasCurrentAnswer && (
                        <div 
                            className="mb-3 p-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(255, 193, 7, 0.3)'
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-white-50 d-flex align-items-center">
                                        <FaHistory className="me-2" />
                                        Poprzednia odpowiedź:
                                    </small>
                                    <div className="text-white fw-semibold mt-1">
                                        {formatPreviousAnswer(currentQuestion, previousAnswer.answer_value)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => applyPreviousAnswer(questionKey)}
                                    className="btn btn-sm"
                                    style={{
                                        background: 'linear-gradient(135deg, #FFC107 0%, #FF9800 100%)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: 'white',
                                        padding: '0.5rem 1rem',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Użyj ponownie
                                </button>
                            </div>
                        </div>
                    )}
                    
                    <div className="d-flex flex-column gap-3">
                        {currentQuestion.answer_options.map(option => (
                            <div
                                key={option.value}
                                onClick={() => !locked && handleAnswerChange(questionKey, { value: option.value })}
                                style={{
                                    padding: '1rem',
                                    borderRadius: '15px',
                                    border: currentAnswer?.value === option.value 
                                        ? '3px solid var(--bs-primary)' 
                                        : '2px solid #e0e0e0',
                                    background: currentAnswer?.value === option.value
                                        ? 'linear-gradient(135deg, rgba(var(--bs-primary-rgb), 0.1) 0%, rgba(0, 86, 179, 0.1) 100%)'
                                        : 'transparent',
                                    cursor: locked ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    opacity: locked ? 0.6 : 1
                                }}
                                className="d-flex align-items-center"
                            >
                                <Form.Check
                                    type="radio"
                                    name={questionKey}
                                    label={option.label}
                                    checked={currentAnswer?.value === option.value}
                                    onChange={() => {}}
                                    disabled={locked}
                                    style={{ pointerEvents: 'none' }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Data rozpoczęcia praktyk
        if (questionType === 'vocational_start_date') {
            return (
                <div>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-white fw-semibold">Rok rozpoczęcia</Form.Label>
                        <Form.Control
                            type="number"
                            min="2000"
                            max={new Date().getFullYear()}
                            value={currentAnswer?.year || ''}
                            onChange={(e) => handleAnswerChange(questionKey, {
                                ...currentAnswer,
                                year: parseInt(e.target.value)
                            })}
                            disabled={locked}
                            style={{
                                borderRadius: '15px',
                                border: '2px solid #e0e0e0',
                                padding: '0.75rem'
                            }}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="text-white fw-semibold">Miesiąc rozpoczęcia</Form.Label>
                        <Form.Select
                            value={currentAnswer?.month || ''}
                            onChange={(e) => handleAnswerChange(questionKey, {
                                ...currentAnswer,
                                month: parseInt(e.target.value)
                            })}
                            disabled={locked}
                            style={{
                                borderRadius: '15px',
                                border: '2px solid #e0e0e0',
                                padding: '0.75rem'
                            }}
                        >
                            <option value="">Wybierz miesiąc</option>
                            {Array.from({ length: 12 }, (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {new Date(2000, i).toLocaleDateString('pl-PL', { month: 'long' })}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    {locked && (
                        <div 
                            className="mt-3 p-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(33, 212, 253, 0.1) 0%, rgba(183, 33, 255, 0.1) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(33, 212, 253, 0.2)'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <FaLock className="me-2" style={{ color: '#21D4FD' }} />
                                <small className="text-white mb-0">
                                    Ta odpowiedź jest zablokowana. Jeśli chcesz ją zmienić, skontaktuj się z administratorem.
                                </small>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Zmiana pracodawcy
        if (questionType === 'repeated_class' || questionType === 'changed_workplace') {
            return (
                <div>
                    {/* POPRZEDNIA ODPOWIEDŹ */}
                    {hasPreviousAnswer && !hasCurrentAnswer && (
                        <div 
                            className="mb-3 p-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(255, 193, 7, 0.3)'
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-white-50 d-flex align-items-center">
                                        <FaHistory className="me-2" />
                                        Poprzednia odpowiedź:
                                    </small>
                                    <div className="text-white fw-semibold mt-1">
                                        {formatPreviousAnswer(currentQuestion, previousAnswer.answer_value)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => applyPreviousAnswer(questionKey)}
                                    className="btn btn-sm"
                                    style={{
                                        background: 'linear-gradient(135deg, #FFC107 0%, #FF9800 100%)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: 'white',
                                        padding: '0.5rem 1rem',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Użyj ponownie
                                </button>
                            </div>
                        </div>
                    )}
                    
                    <div className="d-flex flex-column gap-3 mb-3">
                        {currentQuestion.answer_options.map(option => (
                            <div
                                key={option.value}
                                onClick={() => handleAnswerChange(questionKey, { value: option.value })}
                                style={{
                                    padding: '1rem',
                                    borderRadius: '15px',
                                    border: currentAnswer?.value === option.value 
                                        ? '3px solid var(--bs-primary)' 
                                        : '2px solid #e0e0e0',
                                    background: currentAnswer?.value === option.value
                                        ? 'linear-gradient(135deg, rgba(var(--bs-primary-rgb), 0.1) 0%, rgba(0, 86, 179, 0.1) 100%)'
                                        : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                className="d-flex align-items-center"
                            >
                                <Form.Check
                                    type="radio"
                                    name={questionKey}
                                    label={option.label}
                                    checked={currentAnswer?.value === option.value}
                                    onChange={() => {}}
                                    style={{ pointerEvents: 'none' }}
                                />
                            </div>
                        ))}
                    </div>
                    
                    {questionType === 'changed_workplace' && currentAnswer?.value === 'yes' && (
                        <div 
                            className="p-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(245, 87, 108, 0.1) 0%, rgba(240, 147, 251, 0.1) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(245, 87, 108, 0.3)'
                            }}
                        >
                            <strong className="text-white d-block mb-2">Uwaga!</strong>
                            <p className="text-white mb-0">
                                Zmiana pracodawcy w bieżącym roku szkolnym skutkuje brakiem prawa do urlopu 
                                zgodnie z art. 155 § 1 Kodeksu pracy. Twoja pula urlopowa wyniesie 0 dni.
                            </p>
                        </div>
                    )}
                </div>
            );
        }

        // Wykorzystany urlop / Pozostały urlop
        if (questionType === 'vacation_used' || questionType === 'vacation_remaining') {
            const selectedType = currentAnswer?.type;
            const isRemainingQuestion = questionType === 'vacation_remaining';
            
            // Pobierz sugestię dla vacation_remaining
            const suggestionData = isRemainingQuestion ? questionnaire.vacation_remaining_suggestion : null;
            const hasSuggestion = suggestionData?.has_historical_data && suggestionData?.suggested_days !== undefined;
            
            return (
                <div>
                    {/* POPRZEDNIA ODPOWIEDŹ */}
                    {hasPreviousAnswer && !hasCurrentAnswer && (
                        <div 
                            className="mb-3 p-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(255, 193, 7, 0.3)'
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-white-50 d-flex align-items-center">
                                        <FaHistory className="me-2" />
                                        Poprzednia odpowiedź:
                                    </small>
                                    <div className="text-white fw-semibold mt-1">
                                        {formatPreviousAnswer(currentQuestion, previousAnswer.answer_value)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => applyPreviousAnswer(questionKey)}
                                    className="btn btn-sm"
                                    style={{
                                        background: 'linear-gradient(135deg, #FFC107 0%, #FF9800 100%)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: 'white',
                                        padding: '0.5rem 1rem',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Użyj ponownie
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {/* SUGESTIA DLA VACATION_REMAINING */}
                    {isRemainingQuestion && (
                        <div className="mb-4">
                            {hasSuggestion ? (
                                <div 
                                    className="p-3"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(33, 212, 253, 0.15) 0%, rgba(183, 33, 255, 0.15) 100%)',
                                        borderRadius: '15px',
                                        border: '2px solid rgba(33, 212, 253, 0.3)'
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <small className="text-white-50">💡 Podpowiedź z historii:</small>
                                            <div className="mt-2">
                                                <h5 className="text-white mb-1">
                                                    {suggestionData.suggested_days} dni
                                                </h5>
                                                <small className="text-white-50">
                                                    Dostępne dni z roku {suggestionData.source_year}
                                                </small>
                                            </div>
                                            <div className="mt-2" style={{ fontSize: '0.75rem' }}>
                                                <small className="text-white-50">
                                                    Przyznane: {suggestionData.details.granted}, 
                                                    Przeniesione: {suggestionData.details.carried_over}, 
                                                    Zużyte: {suggestionData.details.used}
                                                </small>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleAnswerChange(questionKey, { 
                                                type: 'exact',
                                                value: suggestionData.suggested_days
                                            })}
                                            className="btn btn-sm"
                                            style={{
                                                background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                                                border: 'none',
                                                borderRadius: '10px',
                                                color: 'white',
                                                padding: '0.5rem 1rem',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            Zaakceptuj
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div 
                                    className="p-3"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
                                        borderRadius: '15px',
                                        border: '2px solid rgba(255, 193, 7, 0.3)'
                                    }}
                                >
                                    <small className="text-white-50">
                                        ⚠️ Brak danych historycznych z poprzedniego roku.
                                    </small>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="d-flex flex-column gap-3">
                        {currentQuestion.answer_options.map(option => (
                            <div key={option.value}>
                                <div
                                    onClick={() => {
                                        if (option.value === 'dont_remember') {
                                            handleAnswerChange(questionKey, { 
                                                type: option.value
                                            });
                                        } else {
                                            handleAnswerChange(questionKey, { 
                                                type: option.value,
                                                value: currentAnswer?.value || 0
                                            });
                                        }
                                    }}
                                    style={{
                                        padding: '1rem',
                                        borderRadius: '15px',
                                        border: selectedType === option.value 
                                            ? '3px solid var(--bs-primary)' 
                                            : '2px solid #e0e0e0',
                                        background: selectedType === option.value
                                            ? 'linear-gradient(135deg, rgba(var(--bs-primary-rgb), 0.1) 0%, rgba(0, 86, 179, 0.1) 100%)'
                                            : 'transparent',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                    className="d-flex align-items-center"
                                >
                                    <Form.Check
                                        type="radio"
                                        name={questionKey}
                                        id={`${questionKey}-${option.value}`}
                                        label={option.label}
                                        checked={selectedType === option.value}
                                        onChange={() => {}}
                                        style={{ pointerEvents: 'none' }}
                                    />
                                </div>
                                
                                {selectedType === option.value && option.value !== 'dont_remember' && (
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max="365"
                                        step="1"
                                        placeholder="Wpisz liczbę dni"
                                        className="mt-2"
                                        style={{ 
                                            maxWidth: '200px',
                                            borderRadius: '15px',
                                            border: '2px solid #e0e0e0',
                                            padding: '0.75rem'
                                        }}
                                        value={currentAnswer?.value ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value === '' ? 0 : parseInt(e.target.value);
                                            handleAnswerChange(questionKey, {
                                                type: option.value,
                                                value: val
                                            });
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    
                    {isRemainingQuestion && selectedType !== 'dont_remember' && (
                        <div 
                            className="mt-3 p-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1) 0%, rgba(56, 249, 215, 0.1) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(67, 233, 123, 0.2)'
                            }}
                        >
                            <small className="text-white">
                                Dni podane w tym pytaniu zostaną dodane do Twojej puli urlopowej w bieżącym roku.
                            </small>
                        </div>
                    )}
                </div>
            );
        }

        return null;
    };

    return (
        <Modal 
            show={show} 
            backdrop="static" 
            keyboard={false} 
            size="lg"
            centered
            contentClassName="border-0"
            onHide={handleCloseModal}
            style={{ borderRadius: '25px' }}
        >
            <div style={{ borderRadius: '25px', overflow: 'hidden' }}>
                <div 
                    className="p-4 d-flex justify-content-between align-items-center"
                    style={{ 
                        background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)'
                    }}
                >
                    <h4 className="text-white fw-bold mb-0">
                        Roczny kwestionariusz urlopowy {questionnaire.session?.year}
                    </h4>
                    <button
                        onClick={handleCloseModal}
                        style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                    >
                        <FaTimes style={{ color: 'white', fontSize: '18px' }} />
                    </button>
                </div>
                
                <Modal.Body className="p-4">
                    {loading && (
                        <div className="text-center py-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Ładowanie...</span>
                            </div>
                        </div>
                    )}
                    
                    {error && (
                        <div 
                            className="p-3 mb-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(245, 87, 108, 0.1) 0%, rgba(240, 147, 251, 0.1) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(245, 87, 108, 0.3)'
                            }}
                        >
                            <strong className="text-danger">{error}</strong>
                        </div>
                    )}
                    
                    {completeError && (
                        <div 
                            className="p-3 mb-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(245, 87, 108, 0.1) 0%, rgba(240, 147, 251, 0.1) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(245, 87, 108, 0.3)'
                            }}
                        >
                            <strong className="text-danger">{completeError}</strong>
                        </div>
                    )}
                    
                    {localError && (
                        <div 
                            className="p-3 mb-3"
                            style={{
                                background: 'linear-gradient(135deg, rgba(245, 87, 108, 0.1) 0%, rgba(240, 147, 251, 0.1) 100%)',
                                borderRadius: '15px',
                                border: '2px solid rgba(245, 87, 108, 0.3)',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            <strong className="text-warning">{localError}</strong>
                        </div>
                    )}

                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted fw-semibold">
                                Pytanie {currentQuestionIndex + 1}/{questions.length}
                            </small>
                            <small className="text-muted fw-semibold">{Math.round(progress)}%</small>
                        </div>
                        <ProgressBar 
                            now={progress}
                            style={{ 
                                height: '10px',
                                borderRadius: '10px',
                                background: '#e0e0e0'
                            }}
                            className="mb-4"
                        >
                            <ProgressBar 
                                now={progress}
                                style={{
                                    background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                                    borderRadius: '10px'
                                }}
                            />
                        </ProgressBar>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentQuestionIndex}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="mb-4">
                                <h5 className="text-white fw-bold mb-3">
                                    {currentQuestion.question_text}
                                </h5>
                                {currentQuestion.help_text && (
                                    <div 
                                        className="p-3 mb-3"
                                        style={{
                                            background: 'linear-gradient(135deg, rgba(33, 212, 253, 0.1) 0%, rgba(183, 33, 255, 0.1) 100%)',
                                            borderRadius: '15px',
                                            border: '2px solid rgba(33, 212, 253, 0.2)'
                                        }}
                                    >
                                        <small className="text-white">{currentQuestion.help_text}</small>
                                    </div>
                                )}
                                {renderQuestionInput()}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </Modal.Body>
                
                <div 
                    className="p-4"
                    style={{ 
                        borderTop: '2px solid #e0e0e0',
                        background: 'rgba(0,0,0,0.02)'
                    }}
                >
                    <div className="d-flex justify-content-between gap-2">
                        <GradientButton
                            onClick={handlePrevious} 
                            disabled={currentQuestionIndex === 0}
                            gradient="pink"
                        >
                            <FaArrowLeft className="me-2" />
                            Wstecz                            
                        </GradientButton>
                        {currentQuestionIndex < questions.length - 1 ? (
                            <GradientButton
                                onClick={handleNext} 
                                gradient="blue"
                            >
                                Następne
                                <FaArrowRight className="ms-2" />                           
                            </GradientButton>
                        ) : (
                            <GradientButton
                                onClick={handleSubmit}
                                gradient="green"
                            >
                                Wyślij kwestionariusz
                            </GradientButton>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default QuestionnaireModal;